import { useDialog } from "hooks/useDialog";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import NewOrdersDialog from "components/NewOrders/NewOrdersDialog/NewOrdersDialog";
import { useNavigate, useParams } from "react-router";
import { useInitOrderDetail } from "hooks/useInitOrderDetail.ts";
import TruckStopModal from "../../Trucks/TruckStopModal/TruckStopModal.tsx";
import AddTruckOrderModal from "../../Trucks/AddTruckOrderModal/AddTruckOrderModal";
import RequestDialog from "../../NewOrders/RequestDialog.tsx";
import DeclineRequestModal from "../../CustomQuote/DeclineRequestModal/DeclineRequestModal.tsx";
import { useDispatch } from "react-redux";
import ConfirmationDialog from "../../Common/ConfirmationDialog/ConfirmationDialog.tsx";
import {
	AreYouSureLeaveAddQuoteText,
	AreYouSureLeaveCreateQuoteText,
	AreYouSureRemoveOrderText,
	AreYouSureRemoveStopDetailText,
	AreYouSureRemoveStopText,
	AreYouSureSubtitleRemoveOrderText,
	AreYouSureTruckText,
	CancelButtonText,
	ConfirmRemoveStopText,
	ContinueQuoteText,
	ContinueRequestText,
	ExitQuoteText,
	ExitTruckText,
	InformationWillBeWipedText,
	NoContinueText,
	RemoveOrderText,
	RemoveTruckConfirm,
	RemoveTruckModalSubtitle,
	RemoveTruckModalTitle,
	RequestToRemoveDraft,
	RequestToRemoveTemplate,
	RequestToRemoveTruckText
} from "constants/text.ts";
import { truckActions } from "features/reducers/truck/truck.ts";
import {
	NewOrderCancelButton,
	NoDoNotCancelButton,
	RemoveCustomQuoteConfirm,
	RemoveCustomQuoteHeader,
	RemoveCustomQuoteSubheader,
	RemoveDraftConfirm,
	RemoveDraftModalSubtitle,
	RemoveDraftModalTitle,
	RemoveTemplateConfirm,
	RemoveTemplateModalSubtitle,
	RemoveTemplateModalTitle,
	RequestCancellationButton,
	RequestToCancelHeaderText,
	RequestToCancelText,
	RequestToRemoveQuote
} from "../../NewOrders/constants.ts";
import { useDrafts } from "hooks/useDrafts.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { SelectCustomColorModal } from "components/NewOrders/SelectCustomColorModal/SelectCustomColorModal.tsx";
import { useCancelOrderMutation } from "features/api/newOrderApi.ts";
import { useTrucks } from "hooks/useTrucks.ts";
import GlobalFormProvider from "components/NewOrders/AddGlobalAttributes/forms/GlobalFormProvider.tsx";
import AddGlobalAttributes from "components/NewOrders/AddGlobalAttributes/AddGlobalAttributes.tsx";
import { useCustomQuotes } from "hooks/useCustomQuotes.ts";
import { useRevisions } from "hooks/useRevisions.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { DraftType, UserRole } from "types/declarations/enums.ts";
import DeclineLeadModal from "features/LeadGen/pages/LeadDetails/components/DeclineLeadModal/DeclineLeadModal.tsx";
import { ReactNode } from "react";

const DialogManager = () => {
	const params = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { dialogStatus, dialogId, dialogIndex, closeDialog, dialogLeavePath, dialogData } = useDialog();
	const { getDraftsTrigger, deleteSelectedDraft, deleteError, resetDeleteSelectedDraft } = useDrafts();
	const { trucksTrigger, deleteTruckError, deleteTruck, resetTruck } = useTrucks();
	const { customQuotesTrigger, deleteCustomQuoteError, deleteCustomQuote, resetCustomQuote } = useCustomQuotes();
	const { data: userDetails } = useGetUserDetailsQuery();
	const isInternalDesigner = userDetails?.role === UserRole.INTERNAL_DESIGNER;
	const { deleteRevision } = useRevisions();

	const { orderDetail: originalOrderDetails } = useInitOrderDetail(params.orderId);
	const [cancelOrder, { error: cancelOrderError, reset: resetCancelOrder }] = useCancelOrderMutation();

	const fallback = () => {};
	const fallbackMutation: any = () => ({
		unwrap: () => Promise.resolve({ data: undefined })
	});

	const removeTruckDraftOrder = () => {
		dispatch(truckActions.removeDraftOrder({ index: dialogIndex, draftOrderId: dialogId }));
		closeDialog();
	};

	const removeTruckStop = () => {
		dispatch(truckActions.removeStop({ index: dialogIndex ?? 0 }));
		closeDialog();
	};

	const leaveTrucksFlow = () => {
		dispatch(truckActions.clearTruck());
		navigate("/trucks");
		closeDialog();
	};

	const leaveCustomQuotesFlow = () => {
		navigate(dialogLeavePath ?? `/custom-quote/${dialogId}`);
		closeDialog();
	};

	const handleDeleteRevision = async () => {
		await deleteRevision({ quoteId: dialogId ?? "", revisionId: dialogData })
			.unwrap()
			.then(() => {
				leaveCustomQuotesFlow();
			})
			.finally(() => {
				closeDialog();
			});
	};

	const dialogMap: Record<string, ReactNode> = {
		[DialogTracking.CSV_UPLOAD]: (
			<NewOrdersDialog
				dialogStatus={DialogTracking.CSV_UPLOAD}
				orderDetails={originalOrderDetails}
				isInternalDesigner={isInternalDesigner}
			/>
		),
		[DialogTracking.SELECT_BILLTO_ACCOUNT]: (
			<NewOrdersDialog
				dialogStatus={DialogTracking.SELECT_BILLTO_ACCOUNT}
				orderDetails={originalOrderDetails}
				isInternalDesigner={isInternalDesigner}
			/>
		),
		[DialogTracking.CANCEL_ORDER]: (
			<RequestDialog
				handleAPISuccess={() => location.reload()}
				apiTrigger={cancelOrder}
				apiError={cancelOrderError}
				title={RequestToCancelHeaderText}
				cancelText={NoDoNotCancelButton}
				confirmText={RequestCancellationButton}
				requestToRemoveText={RequestToCancelText}
				resetApi={resetCancelOrder}
			/>
		),
		[DialogTracking.BUILD_ORDER_OPTIONS]: (
			<NewOrdersDialog
				dialogStatus={DialogTracking.BUILD_ORDER_OPTIONS}
				orderDetails={originalOrderDetails}
				isInternalDesigner={isInternalDesigner}
			/>
		),
		[DialogTracking.ADD_TRUCK_STOP]: <TruckStopModal />,
		[DialogTracking.ADD_TRUCK_ORDER]: <AddTruckOrderModal />,
		[DialogTracking.DELETE_TRUCK]: (
			<RequestDialog
				apiTrigger={deleteTruck}
				apiError={deleteTruckError}
				handleAPISuccess={() => trucksTrigger()}
				title={RemoveTruckModalTitle}
				subtitle={RemoveTruckModalSubtitle}
				cancelText={CancelButtonText}
				confirmText={RemoveTruckConfirm}
				requestToRemoveText={RequestToRemoveTruckText}
				resetApi={resetTruck}
			/>
		),
		[DialogTracking.REMOVE_TRUCK_STOP]: (
			<ConfirmationDialog
				open
				title={AreYouSureRemoveStopText}
				subtitle={AreYouSureRemoveStopDetailText}
				cancelText={CancelButtonText}
				confirmText={ConfirmRemoveStopText}
				onConfirm={removeTruckStop}
				onCancel={closeDialog}
			/>
		),
		[DialogTracking.REMOVE_TRUCK_ORDER]: (
			<ConfirmationDialog
				open
				title={AreYouSureRemoveOrderText}
				subtitle={AreYouSureSubtitleRemoveOrderText}
				cancelText={CancelButtonText}
				confirmText={RemoveOrderText}
				onConfirm={removeTruckDraftOrder}
				onClose={closeDialog}
			/>
		),
		[DialogTracking.LEAVE_TRUCK]: (
			<ConfirmationDialog
				open
				title={AreYouSureTruckText}
				subtitle={InformationWillBeWipedText}
				cancelText={ExitTruckText}
				confirmText={NoContinueText}
				onClose={closeDialog}
				onCancel={leaveTrucksFlow}
				onConfirm={closeDialog}
			/>
		),
		[DialogTracking.LEAVE_ADD_CUSTOM_QUOTE]: (
			<ConfirmationDialog
				open
				title={AreYouSureLeaveAddQuoteText}
				subtitle={InformationWillBeWipedText}
				cancelText={ExitQuoteText}
				confirmText={ContinueQuoteText}
				onClose={closeDialog}
				onCancel={leaveCustomQuotesFlow}
				onConfirm={closeDialog}
			/>
		),
		[DialogTracking.LEAVE_CREATE_CUSTOM_QUOTE]: (
			<ConfirmationDialog
				open
				title={AreYouSureLeaveCreateQuoteText}
				subtitle={InformationWillBeWipedText}
				cancelText={ExitQuoteText}
				confirmText={ContinueRequestText}
				onClose={closeDialog}
				onCancel={leaveCustomQuotesFlow}
				onConfirm={closeDialog}
			/>
		),
		[DialogTracking.REMOVE_DRAFT]: (
			<RequestDialog
				apiTrigger={deleteSelectedDraft ?? fallbackMutation}
				apiError={deleteError ?? undefined}
				handleAPISuccess={getDraftsTrigger}
				title={RemoveDraftModalTitle}
				subtitle={RemoveDraftModalSubtitle}
				cancelText={NewOrderCancelButton.toUpperCase()}
				confirmText={RemoveDraftConfirm}
				requestToRemoveText={RequestToRemoveDraft}
				resetApi={resetDeleteSelectedDraft ?? fallback}
			/>
		),
		[DialogTracking.REMOVE_TEMPLATE]: (
			<RequestDialog
				apiTrigger={deleteSelectedDraft ?? fallbackMutation}
				apiError={deleteError ?? undefined}
				handleAPISuccess={() => {
					getDraftsTrigger(DraftType.Template);
				}}
				title={RemoveTemplateModalTitle}
				subtitle={RemoveTemplateModalSubtitle}
				cancelText={NewOrderCancelButton.toUpperCase()}
				confirmText={RemoveTemplateConfirm}
				requestToRemoveText={RequestToRemoveTemplate}
				resetApi={resetDeleteSelectedDraft ?? fallback}
			/>
		),
		[DialogTracking.SELECT_CUSTOM_COLOR]: <SelectCustomColorModal />,
		[DialogTracking.CUSTOM_QUOTE_ADD_GLOBALS]: (
			<GlobalFormProvider>
				<AddGlobalAttributes
					isCustomColor={false}
					isLandingPage
					isCustomQuote={true}
				/>
			</GlobalFormProvider>
		),
		[DialogTracking.REMOVE_CUSTOM_QUOTE]: (
			<RequestDialog
				apiTrigger={deleteCustomQuote}
				apiError={deleteCustomQuoteError}
				handleAPISuccess={() => customQuotesTrigger("")}
				title={RemoveCustomQuoteHeader}
				subtitle={RemoveCustomQuoteSubheader}
				cancelText={NewOrderCancelButton.toUpperCase()}
				confirmText={RemoveCustomQuoteConfirm}
				requestToRemoveText={RequestToRemoveQuote}
				resetApi={resetCustomQuote}
			/>
		),
		[DialogTracking.DECLINE_CUSTOM_QUOTE]: (
			<DeclineRequestModal
				quoteId={dialogId ?? ""}
				onConfirm={(data) => {}}
			/>
		),
		[DialogTracking.DELETE_CUSTOM_QUOTE_REVISION]: (
			<ConfirmationDialog
				open
				title="Are you sure you want to delete this revision?"
				subtitle={InformationWillBeWipedText}
				cancelText="No, do not delete"
				confirmText="Yes, delete revision"
				onClose={closeDialog}
				onCancel={closeDialog}
				onConfirm={handleDeleteRevision}
			/>
		),
		[DialogTracking.DECLINE_LEAD]: (
			<DeclineLeadModal
				assignmentId={dialogData?.leadAssignmentId ?? ""}
				leadId={dialogData?.leadId ?? ""}
				status={dialogData?.status ?? ""}
				notes={dialogData?.notes ?? ""}
			/>
		)
	};

	return dialogMap[dialogStatus] ?? null;
};

export default DialogManager;
